import { useState, useEffect, useRef } from 'react';
import { Form, Col, Row, Container } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import './Contact.css';

const contactInfo = [
    { icon: '📍', label: 'Address', value: 'Nagpur, Maharashtra, India' },
    { icon: '📞', label: 'Phone', value: '+91 8329025467', href: 'tel:+918329025467' },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="22" height="22" fill="none">
                <circle cx="16" cy="16" r="16" fill="#25D366" />
                <path fill="#fff" d="M23.5 8.5A10.44 10.44 0 0 0 16 5.5C10.75 5.5 6.5 9.75 6.5 15a9.43 9.43 0 0 0 1.4 4.97L6.5 26.5l6.7-1.76A9.5 9.5 0 0 0 16 25.5c5.25 0 9.5-4.25 9.5-9.5a9.43 9.43 0 0 0-2-5Zm-7.5 14.6a7.88 7.88 0 0 1-4.02-1.1l-.29-.17-2.98.78.8-2.91-.19-.3A7.9 7.9 0 0 1 8.1 15a7.9 7.9 0 0 1 7.9-7.9 7.9 7.9 0 0 1 7.9 7.9 7.9 7.9 0 0 1-7.9 7.9Zm4.33-5.9c-.24-.12-1.4-.69-1.61-.77-.22-.08-.38-.12-.54.12-.16.24-.62.77-.76.93-.14.16-.28.18-.52.06a6.57 6.57 0 0 1-1.93-1.19 7.24 7.24 0 0 1-1.34-1.66c-.14-.24 0-.37.1-.49.1-.1.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.4-.54-.41h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.6 4.12 3.64.58.25 1.03.4 1.38.51.58.18 1.11.16 1.52.1.46-.07 1.4-.57 1.6-1.12.2-.55.2-1.02.14-1.12-.06-.1-.22-.16-.46-.28Z" />
            </svg>
        ),
        label: 'WhatsApp',
        value: '+91 8329025467',
        href: 'https://wa.me/918329025467'
    },
    { icon: '✉️', label: 'Email', value: 'christine@leadsqresearch.com' },
];

const COOLDOWN_SEC = 60;

// ── Validators ────────────────────────────────────────────────────────────────
const validators = {
    first_name: (v) => {
        if (!v.trim()) return 'First name is required.';
        if (v.trim().length < 2) return 'First name must be at least 2 characters.';
        if (v.trim().length > 100) return 'First name must be under 100 characters.';
        if (!/^[a-zA-Z\s'-]+$/.test(v)) return 'First name can only contain letters, spaces, hyphens, or apostrophes.';
        return null;
    },
    last_name: (v) => {
        if (!v.trim()) return 'Last name is required.';
        if (v.trim().length < 2) return 'Last name must be at least 2 characters.';
        if (v.trim().length > 100) return 'Last name must be under 100 characters.';
        if (!/^[a-zA-Z\s'-]+$/.test(v)) return 'Last name can only contain letters, spaces, hyphens, or apostrophes.';
        return null;
    },
    email: (v) => {
        if (!v.trim()) return 'Email address is required.';
        if (v.trim().length > 200) return 'Email address must be under 200 characters.';
        // RFC 5322 simplified — catches most invalid emails
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v))
            return 'Please enter a valid email address (e.g. name@domain.com).';
        return null;
    },
    address: (v) => {
        if (!v.trim()) return null; // optional
        if (v.trim().length > 600) return 'Address must be under 600 characters.';
        return null;
    },
    message: (v) => {
        if (!v.trim()) return 'Please write a message before sending.';
        if (v.trim().length < 10) return 'Message is too short. Please provide more detail.';
        if (v.trim().length > 3000) return 'Message must be under 3000 characters.';
        return null;
    },
};

export default function Contact() {
    const [heroVisible, setHeroVisible] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [status, setStatus] = useState(null);
    const [focusedField, setFocused] = useState(null);
    const [honeypot, setHoneypot] = useState('');
    const [cooldown, setCooldown] = useState(0);

    // Field values
    const [fields, setFields] = useState({
        first_name: '',
        last_name: '',
        email: '',
        address: '',
        message: '',
    });

    // Touched tracks which fields the user has interacted with
    // Errors only show after a field is touched (blur) or on submit attempt
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false); // true after first submit attempt

    const form = useRef();
    const formRef = useRef();
    const coolTimer = useRef(null);

    // Hero entrance
    useEffect(() => {
        const t = setTimeout(() => setHeroVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    // Scroll observer
    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setFormVisible(true); },
            { threshold: 0, rootMargin: '0px 0px -60px 0px' }
        );
        if (formRef.current) obs.observe(formRef.current);
        return () => obs.disconnect();
    }, []);

    // Cooldown ticker
    useEffect(() => {
        if (cooldown <= 0) return;
        coolTimer.current = setInterval(() => {
            setCooldown((c) => {
                if (c <= 1) { clearInterval(coolTimer.current); return 0; }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(coolTimer.current);
    }, [cooldown]);

    // Re-validate a field whenever its value changes (only show error if touched)
    const handleChange = (field, value) => {
        const updated = { ...fields, [field]: value };
        setFields(updated);
        if (touched[field] || submitted) {
            const err = validators[field] ? validators[field](value) : null;
            setErrors((prev) => ({ ...prev, [field]: err }));
        }
    };

    // Mark field as touched on blur and validate immediately
    const handleBlur = (field) => {
        setFocused(null);
        setTouched((prev) => ({ ...prev, [field]: true }));
        const err = validators[field] ? validators[field](fields[field]) : null;
        setErrors((prev) => ({ ...prev, [field]: err }));
    };

    // Run all validators, return true if all pass
    const validateAll = () => {
        const newErrors = {};
        ['first_name', 'last_name', 'email', 'address', 'message'].forEach((f) => {
            newErrors[f] = validators[f](fields[f]);
        });
        setErrors(newErrors);
        setTouched({ first_name: true, last_name: true, email: true, address: true, message: true });
        return Object.values(newErrors).every((e) => e === null);
    };

    const formIsValid = ['first_name', 'last_name', 'email', 'address', 'message'].every(
        (f) => !validators[f](fields[f])
    );

    const sendEmail = (e) => {
        e.preventDefault();
        setSubmitted(true);

        if (honeypot) { e.target.reset(); return; }
        if (cooldown > 0) { setStatus('rate-limit'); return; }
        if (!validateAll()) return; // show all errors, stop here

        setStatus('sending');

        emailjs.sendForm(
            process.env.REACT_APP_EMAILJS_GMAIL_SERVICE,
            process.env.REACT_APP_EMAILJS_TEMPLATEID,
            form.current,
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        )
            .then(() => {
                setStatus('success');
                e.target.reset();
                setFields({ first_name: '', last_name: '', email: '', address: '', message: '' });
                setErrors({});
                setTouched({});
                setSubmitted(false);
                setHoneypot('');
                setCooldown(COOLDOWN_SEC);
                setTimeout(() => setStatus(null), 6000);
            })
            .catch(() => {
                setStatus('error');
                setTimeout(() => setStatus(null), 5000);
            });
    };

    const isSubmitDisabled = status === 'sending' || cooldown > 0;

    // Helper: should we show the error for a field?
    const showError = (field) => (touched[field] || submitted) && errors[field];

    return (
        <div className="contact-page">

            {/* ════════ HERO ════════ */}
            <header className={`ct-header height-75 ${heroVisible ? 'ct-header--visible' : ''}`}>
                <div className="ct-header__bg" />
                <div className="ct-header__grid" />
                <div className="ct-header__orb ct-header__orb--1" />
                <div className="ct-header__orb ct-header__orb--2" />
                <Container className="ct-header__content">
                    <div className="ct-hero-badge" style={{ '--delay': '0s' }}>
                        <span className="ct-hero-badge__dot" /> Get In Touch
                    </div>
                    <h1 className="ct-hero-title" style={{ '--delay': '0.18s' }}>
                        Let's <span className="ct-hero-accent">Work</span> Together
                    </h1>
                    <div className="ct-hero-line" style={{ '--delay': '0.32s' }} />
                    <p className="ct-hero-text" style={{ '--delay': '0.42s' }}>
                        Ready to scale your business with precision-targeted leads? Fill out the form
                        below and our team will get back to you with a tailored solution for your needs.
                    </p>
                </Container>
                <div className="ct-header__scroll">
                    <span>Scroll</span>
                    <div className="ct-header__scroll-line" />
                </div>
            </header>

            {/* ════════ FORM + INFO ════════ */}
            <div className={`ct-body ${formVisible ? 'ct-body--visible' : ''}`} ref={formRef}>
                <Container>
                    <div className="ct-row">

                        {/* ── Left: contact info ── */}
                        <div className="ct-info">
                            <span className="ct-label">✦ Contact Details</span>
                            <h2 className="ct-info__title">We'd Love To Hear From You</h2>
                            <p className="ct-info__text">
                                Whether you have a question about our services, pricing, or anything else —
                                our team is ready to answer all your questions.
                            </p>

                            <div className="ct-info__items">
                                {contactInfo.map((c, i) => {
                                    const Tag = c.href ? 'a' : 'div';
                                    return (
                                        <Tag
                                            className="ct-info__item"
                                            key={i}
                                            style={{ '--i': i }}
                                            {...(c.href && { href: c.href, target: '_blank', rel: 'noopener noreferrer' })}
                                        >
                                            <div className="ct-info__icon">{c.icon}</div>
                                            <div>
                                                <span className="ct-info__item-label">{c.label}</span>
                                                <span className="ct-info__item-value">{c.value}</span>
                                            </div>
                                        </Tag>
                                    );
                                })}
                            </div>

                            {/* Privacy assurance */}
                            <div className="ct-privacy">
                                <div className="ct-privacy__icon">🔒</div>
                                <div>
                                    <p className="ct-privacy__title">Your Privacy is Protected</p>
                                    <p className="ct-privacy__text">
                                        The information you share is used <strong>only to respond to your enquiry</strong>.
                                        We never sell, share, or misuse your personal data. No spam, no third parties —
                                        just a direct response from our team.
                                    </p>
                                </div>
                            </div>

                            <div className="ct-info__quote">
                                <div className="ct-info__quote-mark">"</div>
                                <p className="ct-info__quote-text">
                                    Your success is our mission. Every lead we generate is a step toward your growth.
                                </p>
                                <span className="ct-info__quote-author">— LeadsQResearch Team</span>
                            </div>
                        </div>

                        {/* ── Right: form ── */}
                        <div className="ct-form-wrap">
                            <div className="ct-form-card">
                                <div className="ct-form-card__glow" />
                                <h3 className="ct-form-card__title">Send Us a Message</h3>

                                {/* Privacy micro-notice above form */}
                                <div className="ct-form-privacy">
                                    🔒 &nbsp;Your information is safe with us — we only use it to respond to you.
                                </div>

                                <Form ref={form} onSubmit={sendEmail} className="ct-form" noValidate>

                                    {/* Honeypot — invisible to humans, bots fill it */}
                                    <input
                                        type="text"
                                        name="honeypot"
                                        value={honeypot}
                                        onChange={(e) => setHoneypot(e.target.value)}
                                        tabIndex="-1"
                                        autoComplete="off"
                                        aria-hidden="true"
                                        style={{
                                            position: 'absolute', left: '-9999px',
                                            width: '1px', height: '1px',
                                            opacity: 0, pointerEvents: 'none',
                                        }}
                                    />

                                    {/* First + Last name */}
                                    <Row className="mb-0">
                                        <Col sm={12} md={6} className="ct-field-wrap">
                                            <div className={`ct-field
                                                ${focusedField === 'fn' ? 'ct-field--focused' : ''}
                                                ${showError('first_name') ? 'ct-field--error' : ''}
                                                ${!validators.first_name(fields.first_name) && touched.first_name ? 'ct-field--valid' : ''}
                                            `}>
                                                <Form.Label className="ct-field__label">
                                                    First Name <span className="ct-required">*</span>
                                                </Form.Label>
                                                <div className="ct-input-wrap">
                                                    <Form.Control
                                                        type="text"
                                                        name="first_name"
                                                        placeholder="First name"
                                                        className="ct-input"
                                                        maxLength={100}
                                                        value={fields.first_name}
                                                        onChange={(e) => handleChange('first_name', e.target.value)}
                                                        onFocus={() => setFocused('fn')}
                                                        onBlur={() => handleBlur('first_name')}
                                                        isInvalid={!!showError('first_name')}
                                                    />
                                                    {!validators.first_name(fields.first_name) && touched.first_name && (
                                                        <span className="ct-valid-icon">✓</span>
                                                    )}
                                                </div>
                                                {showError('first_name') && (
                                                    <div className="ct-error-msg">⚠ {errors.first_name}</div>
                                                )}
                                                <div className="ct-field__underline" />
                                            </div>
                                        </Col>
                                        <Col sm={12} md={6} className="ct-field-wrap">
                                            <div className={`ct-field
                                                ${focusedField === 'ln' ? 'ct-field--focused' : ''}
                                                ${showError('last_name') ? 'ct-field--error' : ''}
                                                ${!validators.last_name(fields.last_name) && touched.last_name ? 'ct-field--valid' : ''}
                                            `}>
                                                <Form.Label className="ct-field__label">
                                                    Last Name <span className="ct-required">*</span>
                                                </Form.Label>
                                                <div className="ct-input-wrap">
                                                    <Form.Control
                                                        type="text"
                                                        name="last_name"
                                                        placeholder="Last name"
                                                        className="ct-input"
                                                        maxLength={100}
                                                        value={fields.last_name}
                                                        onChange={(e) => handleChange('last_name', e.target.value)}
                                                        onFocus={() => setFocused('ln')}
                                                        onBlur={() => handleBlur('last_name')}
                                                        isInvalid={!!showError('last_name')}
                                                    />
                                                    {!validators.last_name(fields.last_name) && touched.last_name && (
                                                        <span className="ct-valid-icon">✓</span>
                                                    )}
                                                </div>
                                                {showError('last_name') && (
                                                    <div className="ct-error-msg">⚠ {errors.last_name}</div>
                                                )}
                                                <div className="ct-field__underline" />
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Email */}
                                    <div className="ct-field-wrap">
                                        <div className={`ct-field
                                            ${focusedField === 'em' ? 'ct-field--focused' : ''}
                                            ${showError('email') ? 'ct-field--error' : ''}
                                            ${!validators.email(fields.email) && touched.email ? 'ct-field--valid' : ''}
                                        `}>
                                            <Form.Label className="ct-field__label">
                                                Email Address <span className="ct-required">*</span>
                                            </Form.Label>
                                            <div className="ct-input-wrap">
                                                <Form.Control
                                                    type="email"
                                                    name="mail"
                                                    placeholder="your@email.com"
                                                    className="ct-input"
                                                    maxLength={200}
                                                    value={fields.email}
                                                    onChange={(e) => handleChange('email', e.target.value)}
                                                    onFocus={() => setFocused('em')}
                                                    onBlur={() => handleBlur('email')}
                                                    isInvalid={!!showError('email')}
                                                />
                                                {!validators.email(fields.email) && touched.email && (
                                                    <span className="ct-valid-icon">✓</span>
                                                )}
                                            </div>
                                            {showError('email') && (
                                                <div className="ct-error-msg">⚠ {errors.email}</div>
                                            )}
                                            <div className="ct-field__underline" />
                                        </div>
                                        <p className="ct-field__hint">
                                            🔒 We'll never share your email with anyone else.
                                        </p>
                                    </div>

                                    {/* Address (optional) */}
                                    <div className="ct-field-wrap">
                                        <div className={`ct-field
                                            ${focusedField === 'ad' ? 'ct-field--focused' : ''}
                                            ${showError('address') ? 'ct-field--error' : ''}
                                        `}>
                                            <Form.Label className="ct-field__label">
                                                Address <span className="ct-optional">(optional)</span>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Your address"
                                                className="ct-input"
                                                maxLength={600}
                                                value={fields.address}
                                                onChange={(e) => handleChange('address', e.target.value)}
                                                onFocus={() => setFocused('ad')}
                                                onBlur={() => handleBlur('address')}
                                                isInvalid={!!showError('address')}
                                            />
                                            {showError('address') && (
                                                <div className="ct-error-msg">⚠ {errors.address}</div>
                                            )}
                                            <div className="ct-field__underline" />
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div className="ct-field-wrap">
                                        <div className={`ct-field
                                            ${focusedField === 'msg' ? 'ct-field--focused' : ''}
                                            ${showError('message') ? 'ct-field--error' : ''}
                                            ${!validators.message(fields.message) && touched.message ? 'ct-field--valid' : ''}
                                        `}>
                                            <Form.Label className="ct-field__label">
                                                Your Message <span className="ct-required">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="message"
                                                rows={4}
                                                placeholder="Tell us about your project or requirements..."
                                                className="ct-input ct-input--textarea"
                                                maxLength={3000}
                                                value={fields.message}
                                                onChange={(e) => handleChange('message', e.target.value)}
                                                onFocus={() => setFocused('msg')}
                                                onBlur={() => handleBlur('message')}
                                                isInvalid={!!showError('message')}
                                            />
                                            {showError('message') && (
                                                <div className="ct-error-msg">⚠ {errors.message}</div>
                                            )}
                                            <div className="ct-field__underline" />
                                        </div>
                                    </div>

                                    {/* Required fields note */}
                                    <p className="ct-required-note">
                                        <span className="ct-required">*</span> Required fields
                                    </p>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        className={`ct-submit
                                            ${isSubmitDisabled ? 'ct-submit--disabled' : ''}
                                            ${status === 'sending' ? 'ct-submit--sending' : ''}
                                        `}
                                        disabled={isSubmitDisabled}
                                    >
                                        <span className="ct-submit__text">
                                            {status === 'sending'
                                                ? 'Sending…'
                                                : cooldown > 0
                                                    ? `Retry in ${cooldown}s`
                                                    : 'Send Message'}
                                        </span>
                                        {status !== 'sending' && cooldown === 0 && (
                                            <span className="ct-submit__arrow">→</span>
                                        )}
                                        {status === 'sending' && <span className="ct-submit__spinner" />}
                                    </button>

                                    {/* Toasts */}
                                    {status === 'success' && (
                                        <div className="ct-toast ct-toast--success">
                                            ✓ Thanks! We'll get in touch with you soon.
                                        </div>
                                    )}
                                    {status === 'error' && (
                                        <div className="ct-toast ct-toast--error">
                                            ✗ Something went wrong. Please try again.
                                        </div>
                                    )}
                                    {status === 'rate-limit' && (
                                        <div className="ct-toast ct-toast--warn">
                                            ⏳ Please wait {cooldown}s before sending another message.
                                        </div>
                                    )}

                                </Form>
                            </div>
                        </div>

                    </div>
                </Container>
            </div>

        </div>
    );
}
